import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';

const TestimonialsPage = () => {
  const testimonials = [
    {
      id: 1,
      name: 'Priya Sharma',
      location: 'Virginia, USA',
      rating: 5,
      text: '3E Entertainment truly captures the essence of our culture. Each event feels like a reunion with my roots! The attention to detail and authenticity in every celebration brings tears of joy to my eyes.',
      event: 'Dashain Tihar Festival 2024'
    },
    {
      id: 2,
      name: 'Rajesh Kumar',
      location: 'Maryland, USA',
      rating: 5,
      text: 'The performances and celebrations brought back memories from my childhood in Kathmandu. Thank you for creating such magical experiences that connect our children to their heritage!',
      event: '1974 A.D Concert'
    },
    {
      id: 3,
      name: 'Nisha Patel',
      location: 'Washington DC, USA',
      rating: 5,
      text: 'Every festival organized by 3E is a reminder of home. It\'s an honor to be part of this incredible community that celebrates our traditions with such passion and authenticity.',
      event: 'Dashain Tihar Festival 2023'
    },
    {
      id: 4,
      name: 'Arun Thapa',
      location: 'New York, USA',
      rating: 5,
      text: 'As someone who moved to the US 20 years ago, I never thought I\'d experience such authentic Nepali festivals here. 3E Entertainment has created a home away from home for all of us.',
      event: 'Dashain Tihar Festival 2024'
    },
    {
      id: 5,
      name: 'Sunita Gurung',
      location: 'California, USA',
      rating: 5,
      text: 'The community building aspect of these events is incredible. My teenagers, who were losing touch with their roots, now actively participate and are proud of their heritage.',
      event: 'Youth Cultural Workshop'
    },
    {
      id: 6,
      name: 'Binod Shrestha',
      location: 'Texas, USA',
      rating: 5,
      text: 'Professional organization, beautiful cultural presentations, and a warm community atmosphere. 3E Entertainment sets the standard for cultural celebrations in our diaspora.',
      event: 'New Year Celebration 2024'
    }
  ];

  const stats = [
    { number: '500+', label: 'Community Members' },
    { number: '50+', label: 'Events Organized' },
    { number: '25+', label: 'Artists Featured' },
    { number: '98%', label: 'Satisfaction Rate' }
  ];

  const renderStars = (rating) => {
    return Array.from({ length: rating }, (_, i) => (
      <Star key={i} size={16} fill="#ffc107" color="#ffc107" />
    ));
  };

  return (
    <div className="testimonials-page">
      {/* Hero Section */}
      <section className="testimonials-hero">
        <div className="hero-overlay" />
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Testimonials
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Voices from our vibrant community
          </motion.p>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <span className="stat-number">{stat.number}</span>
              <span className="stat-label">{stat.label}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured Testimonial */}
      <section className="featured-section">
        <div className="featured-container">
          <motion.div
            className="featured-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="stars-row">{renderStars(5)}</div>
            <blockquote>
              "3E Entertainment has created something truly special - a space where our culture thrives,
              our children learn their heritage, and our community grows stronger together. Every event
              feels like coming home."
            </blockquote>
            <div className="featured-author">
              <div className="author-avatar">P</div>
              <div className="author-info">
                <h4>Priya Sharma</h4>
                <p>Community Leader, Virginia</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* All Testimonials Grid */}
      <section className="testimonials-grid-section">
        <div className="section-header">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
          >
            What Our Community Says
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            Real stories from real people who have experienced our cultural celebrations
          </motion.p>
        </div>

        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              className="testimonial-card"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5, transition: { duration: 0.3 } }}
            >
              <div className="stars-row">{renderStars(testimonial.rating)}</div>
              <p className="testimonial-text">"{testimonial.text}"</p>
              <div className="event-tag">
                <span>Event:</span> {testimonial.event}
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  {testimonial.name.charAt(0)}
                </div>
                <div className="author-info">
                  <h4>{testimonial.name}</h4>
                  <p>{testimonial.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-overlay" />
        <motion.div
          className="cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
          >
            Ready to Create Your Own Memories?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            Join our community and be part of the cultural celebrations that
            bring us together and keep our heritage alive.
          </motion.p>
          <motion.a
            href="/contact"
            className="cta-btn"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            Join Our Community
            <span className="btn-arrow">→</span>
          </motion.a>
        </motion.div>
      </section>

      <style>{`
        .testimonials-page {
          overflow-x: hidden;
        }

        /* Hero Section */
        .testimonials-hero {
          position: relative;
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("/images/t.jpg") center/cover no-repeat;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          padding: 0 2rem;
        }


        .hero-content h1 {
          font-size: clamp(2.5rem, 6vw, 4rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1rem;
          line-height: 1.1;
        }

        .hero-content p {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
        }

        /* Stats Section */
        .stats-section {
          background: #fff;
          padding: 0;
          margin-top: -50px;
          position: relative;
          z-index: 10;
        }

        .stats-container {
          max-width: 900px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .stat-card {
          padding: 2rem 1.5rem;
          text-align: center;
          border-right: 1px solid #f0f0f0;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .stat-card:last-child {
          border-right: none;
        }

        .stat-card:hover {
          background: #fafafa;
        }

        .stat-number {
          display: block;
          font-size: 2.2rem;
          font-weight: 900;
          color: #C72631;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          font-size: 0.85rem;
          color: #666;
          font-weight: 500;
        }

        /* Featured Section */
        .featured-section {
          padding: 6rem 2rem;
          background: white;
        }

        .featured-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .featured-card {
          background: white;
          border: 2px solid #C72631;
          border-radius: 20px;
          padding: 3rem;
          text-align: center;
        }

        .stars-row {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-bottom: 1.5rem;
        }

        .featured-card .stars-row {
          justify-content: center;
        }

        .featured-card blockquote {
          font-size: 1.25rem;
          font-style: italic;
          line-height: 1.7;
          color: #0a0a0a;
          margin: 0 0 2rem;
          font-weight: 500;
        }

        .featured-author {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
        }

        .author-avatar {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: #C72631;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .author-info {
          text-align: left;
        }

        .author-info h4 {
          color: #0a0a0a;
          font-weight: 700;
          font-size: 1.1rem;
          margin: 0 0 0.25rem;
        }

        .author-info p {
          color: #666;
          font-size: 0.9rem;
          margin: 0;
        }

        /* Testimonials Grid Section */
        .testimonials-grid-section {
          padding: 6rem 2rem;
          background: #f7f7f7;
        }

        .section-header {
          text-align: center;
          max-width: 700px;
          margin: 0 auto 4rem;
        }


        .section-header h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1rem;
        }

        .section-header p {
          font-size: 1.1rem;
          color: #666;
          line-height: 1.7;
        }

        .testimonials-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }

        .testimonial-card {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
          border: 1px solid #f0f0f0;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .testimonial-card:hover {
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
          border-color: transparent;
        }

        .testimonial-text {
          font-size: 0.95rem;
          line-height: 1.7;
          color: #0a0a0a;
          font-style: italic;
          margin: 0 0 1.5rem;
        }

        .event-tag {
          background: #fff9f9;
          padding: 0.75rem 1rem;
          border-radius: 10px;
          margin-bottom: 1.5rem;
          font-size: 0.85rem;
          color: #666;
        }

        .event-tag span {
          color: #C72631;
          font-weight: 600;
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .testimonial-author .author-avatar {
          width: 45px;
          height: 45px;
          font-size: 1.1rem;
        }

        .testimonial-author .author-info h4 {
          font-size: 1rem;
        }

        .testimonial-author .author-info p {
          font-size: 0.85rem;
        }

        /* CTA Section */
        .cta-section {
          position: relative;
          padding: 8rem 2rem;
          background: url("https://images.unsplash.com/photo-1539650116574-75c0c6d0fbdf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80") center/cover no-repeat;
          text-align: center;
        }

        .cta-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .cta-content {
          position: relative;
          z-index: 2;
          max-width: 600px;
          margin: 0 auto;
        }

        .cta-content h2 {
          font-size: clamp(2rem, 4vw, 2.5rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1.5rem;
        }

        .cta-content p {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
          margin: 0 0 2rem;
        }

        .cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: white;
          color: #C72631;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.95rem;
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        }

        .cta-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
        }

        .btn-arrow {
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .cta-btn:hover .btn-arrow {
          transform: translateX(5px);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .testimonials-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .stats-container {
            grid-template-columns: repeat(2, 1fr);
            margin: 0 1rem;
          }

          .stat-card {
            padding: 1.5rem 1rem;
          }

          .stat-card:nth-child(2) {
            border-right: none;
          }

          .stat-card:nth-child(1),
          .stat-card:nth-child(2) {
            border-bottom: 1px solid #f0f0f0;
          }

          .featured-card {
            padding: 2rem;
          }

          .testimonials-grid {
            grid-template-columns: 1fr;
            max-width: 500px;
          }

          .featured-section,
          .testimonials-grid-section,
          .cta-section {
            padding: 4rem 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .stats-container {
            grid-template-columns: 1fr;
          }

          .stat-card {
            border-right: none;
            border-bottom: 1px solid #f0f0f0;
          }

          .stat-card:last-child {
            border-bottom: none;
          }

          .featured-card blockquote {
            font-size: 1.1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default TestimonialsPage;